// src/js/mc/waystoneSort.ts
// Phase 271 (271-07, D-09/D-12, UI-SPEC "Interaction Notes") -- click-to-sort binding for the
// `/mc/<key>/waystones` roster's four headers (Name / Dimension / Coordinates / Discoveries).
//
// Thin DOM assembly on top of the zero-import `utils/rosterSort` module, mirroring how
// `src/js/mc/roster.ts`'s `initRoster()` is thin DOM assembly on top of the SAME module -- this
// file imports `nextSortState`, `parseSortValue`, `sortIndices`, `ariaSortValue` and `sortGlyph`
// UNCHANGED and adds no local re-implementation of any of them, per UI-SPEC's explicit "do not add
// a second, page-specific sort implementation" instruction.
//
// The one genuine gap `rosterSort.ts`'s single-key `RosterSortState` shape has relative to
// UI-SPEC's locked "Coordinates by x then y then z" sort is resolved server-side, not here:
// `McModpackModel::getWaystonesRoster()` pre-computes a single `coord_sort` numeric composite
// (x-then-y-then-z, sign-adjusted) and the template emits it as `data-sort-coords` -- this module
// reads that ONE attribute like any other numeric column and performs no coordinate math of its
// own, matching this codebase's standing convention that every `data-sort-*` value is pre-computed
// by the model and never derived in JS (`detail.latte`'s own six `data-sort-*` attributes follow
// the identical rule).
//
// This table has NO per-player identity column (Discoveries is a bare count, never a discoverer
// name/uuid/list -- D-10/PC-06), so unlike `roster.ts` there is no identity value of any kind for
// this module to read or write.

import {
    nextSortState,
    parseSortValue,
    sortIndices,
    ariaSortValue,
    sortGlyph,
    type RosterSortState,
    type SortKind,
} from '../utils/rosterSort';

interface WaystoneSortController {
    applySort: (state: RosterSortState) => void;
}

// Presence in this map means "already initialised" -- the same idempotency idiom
// `roster.ts`'s own `controllers` WeakMap uses, so a re-entrant call (e.g. a future poll tick)
// is a safe no-op rather than a double-bind.
const controllers = new WeakMap<HTMLTableElement, WaystoneSortController>();

/** `data-sort-kind` is always present on this table's four headers (the template hard-codes it),
 *  but a missing/unrecognized value still degrades to a safe, defined default rather than
 *  `undefined` propagating into `parseSortValue()` -- mirrors `roster.ts`'s own `resolveKind()`. */
function resolveKind(header: HTMLElement): SortKind {
    return header.getAttribute('data-sort-kind') === 'text' ? 'text' : 'numeric';
}

/**
 * Self-guards on `.mc-waystone-table`'s presence FIRST, so this is a no-op on every `/mc` page
 * except Waystones -- the same unconditional-call/self-guard-on-a-page-unique-marker idiom
 * `initFeed()`/`initHeatmap()`/`initAllStats()` already use in `mc.ts`'s own `static init()`.
 */
export function initWaystoneSort(): void {
    const table = document.querySelector<HTMLTableElement>('.mc-waystone-table');
    if (!table) return; // Not the Waystones page (or the roster is currently empty and the table shell was never rendered).

    if (controllers.has(table)) return;

    const tbody = table.tBodies[0];
    if (!tbody) return;

    const headers = Array.from(table.querySelectorAll<HTMLElement>('th[data-sort-key]'));
    if (headers.length === 0) return;

    // INITIAL STATE comes FROM THE DOM, not from an assumed default -- the template hard-codes
    // aria-sort="ascending" + .mc-roster-th--active on the Name header to match
    // getWaystonesRoster()'s own default `ORDER BY name ASC` (D-09/D-12's directory framing).
    // Adopting whichever header is actually marked keeps this file in lockstep with the server
    // even if that default ever changes without this file being touched in the same commit.
    let currentState: RosterSortState = { key: 'name', direction: 'asc' };
    for (const header of headers) {
        const ariaSort = header.getAttribute('aria-sort');
        if (ariaSort === 'ascending' || ariaSort === 'descending') {
            const key = header.getAttribute('data-sort-key');
            if (key) {
                currentState = { key, direction: ariaSort === 'ascending' ? 'asc' : 'desc' };
            }
            break;
        }
    }

    // Do NOT re-sort here -- the server already emitted the rows in currentState's order, and a
    // redundant initial sort is only a chance to silently disagree with it.

    function applySort(state: RosterSortState): void {
        const activeHeader = headers.find((h) => h.getAttribute('data-sort-key') === state.key);
        const kind: SortKind = activeHeader ? resolveKind(activeHeader) : 'numeric';

        const rows = Array.from(tbody!.rows);
        const values = rows.map((tr) => parseSortValue(tr.getAttribute('data-sort-' + state.key), kind));
        const order = sortIndices(values, state.direction);
        for (const i of order) {
            tbody!.appendChild(rows[i]);
        }

        // HEADER SYNC -- ALL headers, on EVERY apply, so a previously-active column can never keep
        // a stale glyph, a stale aria-sort or the active class (same discipline roster.ts's own
        // applySort() enforces).
        for (const header of headers) {
            const isActive = header.getAttribute('data-sort-key') === state.key;
            header.setAttribute('aria-sort', ariaSortValue(isActive, state.direction));
            const icon = header.querySelector<HTMLElement>('.mc-sort-icon');
            if (icon) {
                icon.textContent = sortGlyph(isActive, state.direction);
            }
            header.classList.toggle('mc-roster-th--active', isActive);
        }

        currentState = state;
    }

    for (const header of headers) {
        const key = header.getAttribute('data-sort-key');
        if (!key) continue;
        const kind = resolveKind(header);

        header.addEventListener('click', () => {
            applySort(nextSortState(currentState, key, kind));
        });

        // The template already supplies role="button" and tabindex="0" on every header, so the
        // keyboard focus path exists -- this listener is what makes it actually do something.
        header.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.key === ' ') {
                event.preventDefault();
            }
            applySort(nextSortState(currentState, key, kind));
        });
    }

    controllers.set(table, { applySort });
}
